import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Building2, Plus, Search, Edit2, Trash2, Loader2 } from 'lucide-react'
import { empresaApi } from '@/services/api'
import type { Empresa, PaginatedResult } from '@/types/api'
import { CreateEntityModal } from '@/components/CreateEntityModal'

export default function EmpresasPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingEmpresa, setEditingEmpresa] = useState<Empresa | null>(null)

  const { data, isLoading } = useQuery<PaginatedResult<Empresa>>({
    queryKey: ['empresas', page, search],
    queryFn: () => empresaApi.list({ page, pageSize: 10, search }).then(r => r.data)
  })

  const handleDelete = async (id: string) => {
    if (!confirm(t('empresas.deleteConfirm'))) return
    await empresaApi.delete(id)
    await queryClient.invalidateQueries({ queryKey: ['empresas'] })
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingEmpresa(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('empresas.title')}</h1>
          <p className="text-slate-400 mt-1">{t('empresas.subtitle')}</p>
        </div>
        <button
          onClick={() => { setEditingEmpresa(null); setShowModal(true) }}
          className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          {t('empresas.newEmpresa')}
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder={t('empresas.searchPlaceholder')}
            aria-label={t('empresas.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">{t('empresas.nome')}</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">{t('empresas.taxId')}</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">{t('empresas.email')}</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">{t('empresas.cidade')}</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" aria-label={t('common.loading')} />
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                  {t('empresas.empty')}
                </td>
              </tr>
            ) : (
              data?.items.map((empresa) => (
                <tr key={empresa.id} className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{empresa.nome}</p>
                        <p className="text-slate-500 text-sm">{empresa.nomeFantasia}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-300">{empresa.cnpj}</td>
                  <td className="px-6 py-4 text-slate-300">{empresa.email}</td>
                  <td className="px-6 py-4 text-slate-300">{empresa.cidade}, {empresa.estado}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                      empresa.ativa
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}>
                      {empresa.ativa ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setEditingEmpresa(empresa); setShowModal(true) }}
                        className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                        title={t('common.edit')}
                        aria-label={t('common.edit')}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(empresa.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {data && data.totalPages > 1 && (
          <div className="flex flex-col gap-3 px-6 py-4 border-t border-slate-800 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">
              {t('common.showingResults', { count: data.items.length, total: data.totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-slate-400">{t('common.pageOf', { page, total: data.totalPages })}</span>
              <button
                onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
                className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
      <CreateEntityModal
        kind={showModal ? 'empresa' : null}
        entity={editingEmpresa}
        onClose={closeModal}
      />
    </div>
  )
}
